import { Copy, ShieldCheck, ShieldX } from 'lucide-react';
import { isValidPhoneNumber, parsePhoneNumber } from 'libphonenumber-js';
import * as React from 'react';
import { getTranslate, TranslateFunction } from 'react-localize-redux';
import { toast, ToastContainer } from 'react-toastify';
import styled from 'styled-components';
import Dropdown from '../../../components_v2/dropdown/Dropdown';
import { DropdownStyle } from '../../../components_v2/dropdown/model/Model';
import { getMainPage } from '../../../components_v2/sidebar/Sidebar';
import storeLang from '../../../helpers/storeLang';
import { BorderColor, PastelPrimaryColor, PrimaryColor, RedSidely, SurfaceColor } from '../../../styles/global/css/Utils';
import { Translate, translateToString } from '../../../styles/global/translate';
import { FlexDiv } from '../../products/style';
import { getDialCodeFlag, getPhoneCodeOptions } from '../../settings/userSettings/phoneCountryCodes';
import { getTotp, loginUser, setLogin, setPreferredTotp } from './actions';
import { LoginResult, Totp, TotpApp, TotpMethod } from './model';
import DoodleGroup3 from '../../../styles/custom-icons/doodles/group-3';
import { NoMarginH1, NoMarginP } from '../../authorization/users/Members';
import Input from '../../../components_v2/input/Input';
import { InputStyle } from '../../../components_v2/input/model/Model';
import { DefaultButton } from '../../../styles/global/css/GlobalButton';
import { ButtonStyle } from '../../../components_v2/popup/PopupCreation';
import SidelyText from '../../../styles/custom-icons/sidely-text';
import LanguagePicker from '../../../components_v2/languagePicker/LanguagePicker';

const phoneCodeOptions = getPhoneCodeOptions();

export const emailValid = value =>
	value && !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,10}$/i.test(value)
		? 'Invalid email address'
		: undefined;

const PHONE_INDICATOR_LOGIN_STYLE: DropdownStyle = {
	containerWidth: '130px',
	width: '100%',
	optionWidth: '320px',
	optionHeight: '350px',
	backgroundColor: `color-mix(in oklab, ${PrimaryColor} 10%, transparent)`,
	containerBorder: `1px solid color-mix(in oklab, ${PrimaryColor} 25%, transparent)`,
};

type loadingState = 'loading' | 'loaded' | 'error'

const LanguagePickerContainer = styled.div`
    position: absolute;
    right: 23px;
    top: 10px;
`;

const MethodOptions = styled.div`
    display: grid;
    gap: 12px;
    width: 100%;
    grid-template-columns: 1fr 1fr;
`;

const MethodCard = styled.div<{ selected?: boolean }>`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 16px;
    border-radius: 8px;
    cursor: pointer;
    border: 2px solid ${p => p.selected ? `color-mix(in oklab, ${PrimaryColor} 90%, transparent)` : `color-mix(in oklab, ${PrimaryColor} 30%, transparent)`};
    background: ${p => p.selected ? `color-mix(in oklab, ${PrimaryColor} 15%, transparent)` : `color-mix(in oklab, ${PrimaryColor} 5%, transparent)`};
    font-size: 13px;
    transition: border-color 0.15s, background 0.15s;
    i { font-size: 22px; }
    &:hover {
        border-color: color-mix(in oklab, ${PrimaryColor} 60%, transparent);
        background: color-mix(in oklab, ${PrimaryColor} 10%, transparent);
    }
`;

const OtpContainer = styled.div`
    display: flex;
    gap: 8px;
    justify-content: center;
    input {
        width: 42px;
        height: 48px;
        text-align: center;
        font-size: 20px;
        font-weight: 600;
        border: 2px solid color-mix(in oklab, ${PrimaryColor} 30%, transparent);
        border-radius: 8px;
        background: color-mix(in oklab, ${PrimaryColor} 10%, transparent);
        color: ${PrimaryColor};
        outline: none;
        caret-color: transparent;
        &:focus {
            border-color: color-mix(in oklab, ${PrimaryColor} 80%, transparent);
            background: color-mix(in oklab, ${PrimaryColor} 15%, transparent);
        }
    }
`;

const TOAST_OPTIONS = {
	position: 'top-right' as const,
	autoClose: 10000,
	hideProgressBar: false,
	closeOnClick: true,
	pauseOnHover: true,
	draggable: true,
	progress: undefined,
};

function launchToastError(message: string) {
	console.log('LAUNCH EROR !');
	const translate = getTranslate(storeLang.getState().localize);
	toast.error(translate(message) as string, TOAST_OPTIONS);
}

const BACKEND_ERROR_KEYS: Record<string, string> = {
	'User doesn\'t exist': 'login.error_user_not_found',
	'Code sent too recently': 'login.error_code_too_recent',
	'Too many code sent, please wait': 'login.error_too_many_codes',
};

function launchAxiosError(e: unknown, fallbackKey: string) {
	const data = (e as any)?.response?.data;
	const backendMessage = data?.message ?? data?.error ?? data?.detail ?? (typeof data === 'string' ? data : undefined);
	if (!backendMessage) {
		launchToastError(fallbackKey);
		return;
	}
	const translationKey = BACKEND_ERROR_KEYS[backendMessage];
	if (translationKey) {
		launchToastError(translationKey);
	} else {
		toast.error(backendMessage, TOAST_OPTIONS);
	}
}


function TotpForm(props: { code: string, setCode: (v: string) => void, onLogin: () => void, onSubmit: () => void, totp: Totp, setTotp: (v: Totp | undefined) => void, loadingState: loadingState, translate: TranslateFunction, email: string, onSetTotp, phone, setPhone, phoneIndicator: string, setPhoneIndicator: (v: string) => void, method, setMethod, totpApp, displayQrCode, setDisplayQrCode, loginResult }) {
	const { setCode, onLogin, onSubmit, loadingState, translate, totp, setTotp, email, onSetTotp, phone, setPhone, phoneIndicator, setPhoneIndicator, method, setMethod, totpApp, displayQrCode, setDisplayQrCode, loginResult } = props;
	const phoneIsInvalid = method === 'Sms' && phone.length > 0
		? !isValidPhoneNumber(`+${phoneIndicator}${phone}`)
		: false;
	const [digits, setDigits] = React.useState<string[]>(Array(6).fill(''));
	const inputRefs = React.useRef<Array<HTMLInputElement | null>>(Array(6).fill(null));
	const [resendCooldown, setResendCooldown] = React.useState<number>(20);

	React.useEffect(() => {
		if (resendCooldown <= 0) return;
		const timer = setTimeout(() => setResendCooldown(c => c - 1), 1000);
		return () => clearTimeout(timer);
	}, [resendCooldown]);

	function updateDigit(index: number, value: string) {
		const newDigits = [...digits];
		newDigits[index] = value;
		setDigits(newDigits);
		setCode(newDigits.join(''));
		if (value && index < 5) {
			inputRefs.current[index + 1]?.focus();
		}
	}

	function handleChange(index: number, value: string) {
		updateDigit(index, value.replace(/\D/g, '').slice(-1));
	}

	function handleKeyDown(index: number, e: React.KeyboardEvent<HTMLInputElement>) {
		if (e.key === 'Enter') {
			onLogin();
		} else if (e.key === 'Backspace' && !digits[index] && index > 0) {
			updateDigit(index - 1, '');
			inputRefs.current[index - 1]?.focus();
		}
	}

	function handlePaste(e: React.ClipboardEvent) {
		e.preventDefault();
		const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
		const newDigits = Array.from({ length: 6 }, (_, i) => pasted[i] ?? '');
		setDigits(newDigits);
		setCode(newDigits.join(''));
		inputRefs.current[Math.min(pasted.length, 5)]?.focus();
	}

	return totp.method === 'Unset' ?
		<>
			<NoMarginP>
				<Translate id='login.preferred_totop' />
			</NoMarginP>
			<MethodOptions>
				<MethodCard selected={method === 'Application'} onClick={() => setMethod('Application')}>
					<ShieldCheck size={26} />
					<NoMarginP><Translate id='login.method_app' /></NoMarginP>
				</MethodCard>
				<MethodCard onClick={() => setLogin(loginResult.token_info, loginResult.policy)
					.then(res => window.location.href = getMainPage(res))
					.catch(console.log)}>
					<ShieldX size={26} />
					<NoMarginP><Translate id='login.skip' /></NoMarginP>
				</MethodCard>
			</MethodOptions>
			{method === 'Sms' &&
				<div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
					<Dropdown
						name='totp_phone_indicator'
						datalist={phoneCodeOptions}
						selectedValue={phoneCodeOptions.find(o => o.value === phoneIndicator)}
						placeholder='+--'
						searchable
						dropdownStyle={PHONE_INDICATOR_LOGIN_STYLE}
						JSXButtonInsideContainer
						JSXButton={({ value }) => (
							<span style={{ padding: '0 8px', fontSize: '14px' }}>
								{value ? `${getDialCodeFlag(value.value as string)} +${value.value}` : '+--'}
							</span>
						)}
						onChange={option => {
							if (Array.isArray(option)) return;
							setPhoneIndicator(option.value);
						}}
					/>
					<div className='form-group' style={{ flex: 1, marginBottom: 0 }}>
						<input
							className='form-control'
							name='totp_phone'
							type='tel'
							required
							placeholder={translate('login.phone_placeholder') as string}
							value={phone}
							onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPhone(e.target.value)}
							style={phoneIsInvalid ? { borderColor: RedSidely, boxShadow: `0 0 0 2px color-mix(in oklab, ${RedSidely} 25%, transparent)` } : undefined}
						/>
					</div>
				</div>
			}
			<DefaultButton
				buttonStyle={ButtonStyle.Primary}
				width='320px'
				height='40px'
				onClick={onSetTotp}
				disabled={loadingState === 'loading' || method === 'Unset' || (method === 'Sms' && (!phone || phoneIsInvalid))}
			>
				<Translate id='login.confirm' />
			</DefaultButton>
		</>
		:
		<>
			{(totp.method === 'Sms' || totp.method === 'Email') &&
				<div style={{ fontSize: '14px' }}>
					<Translate id='login.totp_msg' options={{ variables: [['TOTPMETHOD', totp.method === 'Sms' ? totp.phone : email]] }} />
				</div>
			}
			{displayQrCode ?
				<FlexDiv flow='column' justify='center' gap='1.5rem' margin='0 0 10px 0'>
					<div style={{ fontSize: '14px' }}>
						<Translate id='login.totp_msg_application_scan' />
					</div>
					<img style={{ width: '100%', maxWidth: '180px' }} alt="" src={`data:image/png;base64,${totpApp.qr_code}`} />
					<FlexDiv align='center' gap='8px' width='100%'>
						<FlexDiv fontSize='10px' border={`2px solid color-mix(in oklab, ${PrimaryColor} 30%, transparent)`} backgroundColor={`color-mix(in oklab, ${PrimaryColor} 5%, transparent)`} padding='10px' borderRadius='5px' style={{ flex: 1, wordBreak: 'break-all' }}>{totpApp.secret}</FlexDiv>
						<Copy size={20} style={{ cursor: 'pointer', flexShrink: 0 }} onClick={() => navigator.clipboard.writeText(totpApp.secret).then(() => toast.success(translate('copied_in_clipboard') as string))} />
					</FlexDiv>
					<div className='text-right' style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
						<DefaultButton
							margin='0'
							height='40px'
							onClick={() => setTotp({ ...totp, method: 'Unset' })}>
							<Translate id='back' />
						</DefaultButton>
						<DefaultButton
							margin='0'
							buttonStyle={ButtonStyle.Primary}
							height='40px'
							onClick={() => setDisplayQrCode(false)}>
							<Translate id='next' />
						</DefaultButton>
					</div>
				</FlexDiv>
				:
				<>
					{totp.method === 'Application' &&
						<NoMarginP>
							<Translate id='login.totp_msg_application' />
						</NoMarginP>
					}
					<OtpContainer>
						{Array.from({ length: 6 }, (_, i) => (
							<input
								key={i}
								ref={el => { inputRefs.current[i] = el; }}
								type='text'
								inputMode='numeric'
								maxLength={1}
								value={digits[i]}
								onChange={e => handleChange(i, e.target.value)}
								onKeyDown={e => handleKeyDown(i, e)}
								onPaste={handlePaste}
								onFocus={e => e.target.select()}
							/>
						))}
					</OtpContainer>
					<div className="text-right" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
						<FlexDiv gap='8px' width='100%'>
							{method === 'Application' && (
								<DefaultButton onClick={() => setDisplayQrCode(true)}
									margin='0'
									height='40px'
								>
									<Translate id='back' />
								</DefaultButton>
							)}
							<DefaultButton
								buttonStyle={ButtonStyle.Primary}
								width='320px'
								height='40px'
								margin='0'
								onClick={onLogin}
							><Translate id='login.submit' /></DefaultButton>
						</FlexDiv>
					</div>
					{totp.method === 'Sms' && (
						<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px' }}>
							{resendCooldown > 0 ?
								<FlexDiv style={{ fontSize: '13px' }}><Translate id='login.resend_code' /> <Translate id='login.enabled_in' /> {`(${resendCooldown})`}</FlexDiv>
								:
								<span
									style={{ color: `color-mix(in oklab, ${PrimaryColor} 70%, transparent)`, fontSize: '13px', cursor: 'pointer', textDecoration: 'underline' }}
									onClick={() => {
										if (resendCooldown > 0) return;
										onSubmit();
										setResendCooldown(30);
									}}
								>
									<Translate id='login.resend_code' />
								</span>
							}
						</div>
					)}
				</>
			}
		</>;
};

export function LoginLanguagePicker() {
	return <LanguagePickerContainer><LanguagePicker /></LanguagePickerContainer>;
}

export const PageWrapper = styled.div`
	position: relative;
	height: 100dvh;
	overflow: hidden;
	background: ${PastelPrimaryColor};
	display:flex;
	justify-content: center;
	align-items: center;
`;

export const FormWrapper = styled.div`
	background-color: ${SurfaceColor};
	padding: 50px;
  	border-radius: 10px;
   	border: 1px solid ${BorderColor};
    justify-content: center;
    display: flex;
    flex-flow: column;
    align-items: center;
    gap: 20px;
`;

export const FormWrapperForm = FormWrapper.withComponent('form');

const SidelyLogoWrapper = styled.div`
	display: flex;
	align-items: center;
	margin-bottom: 20px;
	gap: 2px;
`;

export const LoginText = styled(NoMarginP)`
	max-width: 320px;
	text-align: center;
`;

export function LoginLogo() {
	return <SidelyLogoWrapper>
		<DoodleGroup3 height={27} width='unset' />
		<SidelyText height={40} width='unset' />
	</SidelyLogoWrapper>;
}

const ForgottenPasswordLink = styled.a`
	position: absolute;
  	top: 0;
  	right: 0;
  	font-size: 11px;
   	color: ${PrimaryColor};
`;

export const LOGIN_INPUT_STYLE: InputStyle = { labelUp: true, width: '320px', containerHeight: 'fit-content' };

export default function Login() {
	const translate = getTranslate(storeLang.getState().localize);
	const [email, setEmail] = React.useState<string>('');
	const [password, setPassword] = React.useState<string>('');
	const [code, setCode] = React.useState<string>('');
	const [loadingState, setLoadingState] = React.useState<loadingState>('loaded');
	const [totp, setTotp] = React.useState<Totp | undefined>(undefined);
	const [method, setMethod] = React.useState<TotpMethod>('Unset');
	const [phone, setPhone] = React.useState<string>('');
	const [phoneIndicator, setPhoneIndicator] = React.useState<string>('33');
	const [totpApp, setTotpApp] = React.useState<TotpApp | undefined>(undefined);
	const [displayQrCode, setDisplayQrCode] = React.useState<boolean>(false);
	const [loginResult, setLoginResult] = React.useState<LoginResult | undefined>(undefined);
	const [sessionId, setSessionId] = React.useState<string | undefined>(undefined);

	function onSubmit(session?: string) {
		if (!email) {
			launchToastError('login.no_email');
		} else if (emailValid(email) !== undefined) {
			launchToastError('login.email_not_well_formated');
		} else if (!password) {
			launchToastError('login.no_password');
		} else {
			getTotp({
				email,
				password,
			}, session ?? sessionId).then(res => {
				setLoadingState('loaded');
				setTotp({ method: res.method, phone: res.phone });
				if (res.sign_in_response) {
					setLoginResult(res.sign_in_response);
				}
			})
				.catch(e => {
					setLoadingState('error');
					launchAxiosError(e, 'login.wrong_password');
				});
		}
	}

	function onLogin() {
		loginUser({
			email,
			password,
			code,
		}, sessionId)
			.then(res => {
				setLoadingState('loaded');
				setLogin(res.sign_in_response.token_info, res.sign_in_response.policy)
					.then(res => window.location.href = getMainPage(res))
					.catch(console.log);
			})
			.catch(e => {
				setLoadingState('error');
				launchAxiosError(e, 'login.wrong_totp_code');
			});
	}

	function onSetTotp() {
		if (method === 'Sms' && phone && phoneIndicator) {
			if (!isValidPhoneNumber(`+${phoneIndicator}${phone}`)) {
				launchToastError('invalid_phone_number');
				return;
			}
		}
		const e164Phone = method === 'Sms' && phone && phoneIndicator
			? parsePhoneNumber(`+${phoneIndicator}${phone}`).format('E.164').slice(1)
			: phone;
		setPreferredTotp({ login: { email, password }, method, phone: e164Phone })
			.then(res => {
				setSessionId(res.session);
				switch (method) {
					case 'Application':
						setTotp({ method: 'Application', phone: '' });
						if (res.qr_code)
							setTotpApp(res);
						setDisplayQrCode(true);
						break;
					case 'Sms':
						onSubmit(res.session);
						setTotp({ method: 'Sms', phone: phone });
				}
			});
	}

	return <PageWrapper>
		<ToastContainer
			position="top-right"
			autoClose={10000}
			hideProgressBar={false}
			newestOnTop
			closeOnClick
			rtl={false}
			pauseOnFocusLoss
			draggable
			pauseOnHover
		/>
		<FormWrapper>
			<LoginLogo />
			{totp ?
				<TotpForm
					code={code}
					setCode={setCode}
					onLogin={onLogin}
					onSubmit={onSubmit}
					totp={totp}
					setTotp={setTotp}
					loadingState={loadingState}
					translate={translate}
					email={email}
					phone={phone}
					onSetTotp={onSetTotp}
					setPhone={setPhone}
					phoneIndicator={phoneIndicator}
					setPhoneIndicator={setPhoneIndicator}
					method={method}
					setMethod={setMethod}
					totpApp={totpApp}
					displayQrCode={displayQrCode}
					setDisplayQrCode={setDisplayQrCode}
					loginResult={loginResult}
				/>
				:
				<>
					<NoMarginH1>
						<Translate id='login.welcome' />
					</NoMarginH1>
					<NoMarginP>
						<Translate id='login.connect_to_your_space' />
					</NoMarginP>
					<Input
						name='email'
						type='email'
						label='Email'
						inputStyle={LOGIN_INPUT_STYLE}
						value={email}
						onChange={value => setEmail(value)}
					/>
					<div style={{ position: 'relative' }}>
						<Input
							name='password'
							type='password'
							label={translateToString('login.password')}
							inputStyle={LOGIN_INPUT_STYLE}
							value={password}
							onChange={value => setPassword(value)}
						/>
						<ForgottenPasswordLink
							href="/password/forgot"
						>
							<Translate id="forgotten password" />?
						</ForgottenPasswordLink>
					</div>
					<DefaultButton
						buttonStyle={ButtonStyle.Primary}
						width='320px'
						height='40px'
						onClick={() => onSubmit()}
					><Translate id='login.sign_in' /></DefaultButton>
				</>}
		</FormWrapper>
		<LoginLanguagePicker />
	</PageWrapper>;
}
